import axios from "./AxiosClient";
import { Constants } from "@socialcoach/services";
import {
  Page,
  Player,
  PlayerSearchDir,
  PlayerSearchQuery,
  PlayerSearchResult,
  PlayerSearchSort,
  PlayerShortLinkSource,
  SocialMention,
  SocialMentionSearchQuery,
  SocialMentionSearchResult,
} from "../../services/bedrock";
import { scCatchServiceException } from "../../annotations";
import { CancelRequestOptions } from ".";
import { SocialNetworksId, PricePlanTier } from "@/interfaces";

const PLAYERS_SERVICE = `${Constants.API_URL}/bedrock/players`;
export type PlayerFilters = {
  search?: string;
  hasPlayerCard?: boolean;
  eligibilityType?: string;
  eligibilityId?: string | number;
  extraFields?: string[] | string;
  page?: number;
  perPage?: number;
  sort?: PlayerSearchSort;
  sortDirection?: PlayerSearchDir;
};

export type UpdatePlayerParams = {
  allowPlayerAccessToPlayerCardsPermission?: boolean;
  sendPlayerCardNotification?: boolean;
  webAddress?: string | null;
  shortLink?: {
    slug?: string | null;
    source?: PlayerShortLinkSource;
  };
  aliases?: string[];
};

export type CreatePlayerParams = {
  subscriptionTier: PricePlanTier;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  title?: string;
  complianceDisclaimer?: string;
  internalIdentifier?: string;
  branchNames?: string[];
  regionNames?: string[];
  stateCodes?: string[];
  costCenterNames?: string[];
  industryRoleNames?: string[];
  skipOnboardingEmail?: boolean;
};

export class PlayersServices {
  @scCatchServiceException()
  static async createPlayer(player: CreatePlayerParams): Promise<Player> {
    const { data } = await axios.post<Player>(PLAYERS_SERVICE, {
      ...player,
    });
    return data;
  }

  @scCatchServiceException()
  static async getPlayers(filters: PlayerFilters = {}, options?: CancelRequestOptions): Promise<Page<Player>> {
    const url = PLAYERS_SERVICE;
    filters.page = filters.page || 1;
    filters.perPage = filters.perPage || 25;
    const { data } = await axios.get(url, {
      params: filters,
      ...options,
    });
    return data;
  }

  @scCatchServiceException()
  static async getPlayer(playerId: number | string, extraFields?: string[] | string): Promise<Player> {
    const url = `${PLAYERS_SERVICE}/${playerId}`;
    const { data } = await axios.get(url, {
      params: { extraFields },
    });
    return data;
  }

  @scCatchServiceException()
  static async updatePlayer(playerId: number | string, params: UpdatePlayerParams): Promise<Player> {
    const url = `${PLAYERS_SERVICE}/${playerId}`;
    const { data } = await axios.patch(url, params);
    return data;
  }

  @scCatchServiceException()
  static async searchPlayers(
    query: PlayerSearchQuery,
    options?: CancelRequestOptions
  ): Promise<Page<PlayerSearchResult>> {
    const url = `${PLAYERS_SERVICE}/search`;
    const { data } = await axios.post(url, query, options);
    return data;
  }

  @scCatchServiceException()
  static async getPreviousSocialMentions(
    playerId: number,
    socialNetworkId: SocialNetworksId
  ): Promise<Page<SocialMention>> {
    const { data } = await axios.get(`${PLAYERS_SERVICE}/${playerId}/social_mentions`, { params: { socialNetworkId } });
    return data;
  }

  @scCatchServiceException()
  static async searchSocialMentions(
    playerId: number,
    query: SocialMentionSearchQuery
  ): Promise<SocialMentionSearchResult[]> {
    const { data } = await axios.post(`${PLAYERS_SERVICE}/${playerId}/social_mentions/search`, query);
    return data;
  }
}
